import * as React from "react";
import * as XLSX from "xlsx";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, PackageSearch, RefreshCw, Download, Loader2 } from "lucide-react";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";
import { formatCurrencyAmount } from "@/lib/currency";
import { isUsableCost, type CanonicalStockRow } from "@/features/nebim/parseStock";

type Batch = {
  id: string;
  original_filename: string | null;
  status: string;
  total_rows: number | null;
  valid_rows: number | null;
  error_rows: number | null;
  created_at: string;
  error_summary: unknown;
};

type SnapshotRow = {
  id: string;
  source_row_number: number;
  row_status: string;
  normalized_payload: CanonicalStockRow | null;
};

const PAGE_SIZE = 1000;

function formatQuantity(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(value);
}

function variantKey(row: CanonicalStockRow): string {
  return row.barcode?.trim() || [row.sku, row.color_code, row.size].map((v) => v ?? "").join("|");
}

const Inventory: React.FC = () => {
  const { companyId } = useAuth();
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [batch, setBatch] = React.useState<Batch | null>(null);
  const [rows, setRows] = React.useState<SnapshotRow[]>([]);
  const [postSnapshotMovements, setPostSnapshotMovements] = React.useState<Array<{ invoice_no: string; product_code: string; product_name: string; quantity: number; net_cost: number }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [location, setLocation] = React.useState("all");
  const loadRequestId = React.useRef(0);

  const loadSnapshot = React.useCallback(async () => {
    const requestId = ++loadRequestId.current;
    if (!factory.ok || !companyId) {
      setBatch(null); setRows([]); setPostSnapshotMovements([]); setError(null); setLoading(false);
      return;
    }
    setLoading(true); setError(null); setBatch(null); setRows([]); setPostSnapshotMovements([]);
    try {
      // Search beyond fixture files instead of only checking the newest 100 raw batches.
      const BATCH_PAGE_SIZE = 500;
      const BATCH_SCAN_CAP = 10000;
      let latest: Batch | undefined;
      let scannedCount = 0;
      let scannedAllAvailable = false;
      for (let start = 0; start < BATCH_SCAN_CAP; start += BATCH_PAGE_SIZE) {
        const { data, error: batchError } = await factory.client.from("import_batches")
          .select("id, original_filename, status, total_rows, valid_rows, error_rows, created_at, error_summary")
          .eq("company_id", companyId).eq("import_type", "nebim_stock_snapshot").eq("status", "committed")
          .order("created_at", { ascending: false }).range(start, start + BATCH_PAGE_SIZE - 1);
        if (requestId !== loadRequestId.current) return;
        if (batchError) throw batchError;
        const page = (data ?? []) as Batch[];
        scannedCount += page.length;
        latest = page[0];
        if (latest) break;
        if (page.length < BATCH_PAGE_SIZE) { scannedAllAvailable = true; break; }
      }
      if (!latest && !scannedAllAvailable && scannedCount >= BATCH_SCAN_CAP) {
        throw new Error("Son 10.000 stok aktarımı tarandı ancak gerçek IFM snapshot bulunamadı. Eksik veriyi yokmuş gibi göstermemek için rapor durduruldu.");
      }
      if (!latest) { setBatch(null); setRows([]); return; }
      const collected: SnapshotRow[] = [];
      let rowsComplete = false;
      for (let start = 0; start < 20000; start += PAGE_SIZE) {
        const { data, error: rowError } = await factory.client.from("import_rows")
          .select("id, source_row_number, row_status, normalized_payload")
          .eq("company_id", companyId).eq("batch_id", latest.id)
          .order("source_row_number", { ascending: true }).range(start, start + PAGE_SIZE - 1);
        if (requestId !== loadRequestId.current) return;
        if (rowError) throw rowError;
        const page = (data ?? []) as SnapshotRow[];
        collected.push(...page);
        if (page.length < PAGE_SIZE) { rowsComplete = true; break; }
      }
      if (!rowsComplete) throw new Error("Stok raporu 20.000 satır sınırına ulaştı. Eksik toplam göstermemek için rapor durduruldu; veriyi bölerek aktarın.");
      // Show finalized invoice stock entries posted after this snapshot separately.
      // Do not add product-level entries to variant rows: many invoices are mapped
      // without a size/color variant, so that would duplicate quantities across sizes.
      const { data: postingData, error: postingError } = await factory.client
        .from("supplier_invoice_stock_postings")
        .select("invoice_id, product_id, quantity, line_subtotal, posted_at")
        .eq("company_id", companyId)
        .gt("posted_at", latest.created_at)
        .order("posted_at", { ascending: true });
      if (postingError) throw postingError;
      const postings = (postingData ?? []) as Array<{ invoice_id: string; product_id: string; quantity: number | string; line_subtotal: number | string; posted_at: string }>;
      const invoiceIds = Array.from(new Set(postings.map((p) => p.invoice_id)));
      const productIds = Array.from(new Set(postings.map((p) => p.product_id)));
      const [{ data: invoiceData, error: invoiceError }, { data: productData, error: productError }] = await Promise.all([
        invoiceIds.length ? factory.client.from("supplier_invoices").select("id, invoice_no, status, test_only").eq("company_id", companyId).in("id", invoiceIds) : Promise.resolve({ data: [], error: null }),
        productIds.length ? factory.client.from("products").select("id, product_code, product_name").eq("company_id", companyId).in("id", productIds) : Promise.resolve({ data: [], error: null }),
      ]);
      if (invoiceError) throw invoiceError;
      if (productError) throw productError;
      const validInvoices = new Map<string, string>();
      for (const invoice of (invoiceData ?? []) as Array<{ id: string; invoice_no: string | null; status: string; test_only: boolean }>) {
        if (invoice.status === "finalized" && !invoice.test_only) {
          validInvoices.set(invoice.id, invoice.invoice_no ?? "Fatura");
        }
      }
      const productById = new Map<string, { id: string; product_code: string | null; product_name: string | null }>();
      for (const product of (productData ?? []) as Array<{ id: string; product_code: string | null; product_name: string | null }>) {
        productById.set(product.id, product);
      }
      const grouped = new Map<string, { invoice_no: string; product_code: string; product_name: string; quantity: number; net_cost: number }>();
      for (const posting of postings) {
        const invoiceNo = validInvoices.get(posting.invoice_id);
        const product = productById.get(posting.product_id);
        if (!invoiceNo || !product) continue;
        const key = `${invoiceNo}| ${product.product_code}`;
        const current = grouped.get(key) ?? { invoice_no: invoiceNo, product_code: product.product_code ?? "—", product_name: product.product_name ?? "Ürün", quantity: 0, net_cost: 0 };
        current.quantity += Number(posting.quantity) || 0;
        current.net_cost += Number(posting.line_subtotal) || 0;
        grouped.set(key, current);
      }
      if (requestId === loadRequestId.current) {
        setBatch(latest);
        setRows(collected);
        setPostSnapshotMovements(Array.from(grouped.values()).sort((a, b) => a.product_code.localeCompare(b.product_code)));
      }
    } catch (e) {
      if (requestId !== loadRequestId.current) return;
      const err = e as { message?: unknown };
      setBatch(null); setRows([]); setPostSnapshotMovements([]);
      setError(typeof err?.message === "string" ? err.message : "Stok snapshot kaydı yüklenemedi.");
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [factory, companyId]);

  React.useEffect(() => {
    void loadSnapshot();
    return () => { loadRequestId.current += 1; };
  }, [loadSnapshot]);

  const snapshotRows = React.useMemo(
    () => rows.filter((row) => row.normalized_payload).map((row) => ({ ...row, stock: row.normalized_payload as CanonicalStockRow })),
    [rows],
  );
  const locations = React.useMemo(
    () => Array.from(new Set(snapshotRows.map((row) => row.stock.store_name).filter((value): value is string => Boolean(value)))).sort(),
    [snapshotRows],
  );
  const filteredRows = React.useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    return snapshotRows.filter(({ stock }) => {
      if (location !== "all" && stock.store_name !== location) return false;
      if (!query) return true;
      return [stock.sku, stock.barcode, stock.product_name, stock.color_code, stock.color_name, stock.size, stock.store_name]
        .some((value) => String(value ?? "").toLocaleLowerCase("tr-TR").includes(query));
    });
  }, [snapshotRows, search, location]);

  // Validation-error rows remain visible for troubleshooting, but must not
  // inflate the headline inventory totals or cost valuation.
  const validSnapshotRows = snapshotRows.filter(({ row_status }) => row_status !== "error");
  const uniqueVariants = new Set(validSnapshotRows.map(({ stock }) => variantKey(stock))).size;
  const totalInventory = validSnapshotRows.reduce((sum, { stock }) => sum + (stock.inventory_qty ?? 0), 0);
  const availableInventory = validSnapshotRows.reduce((sum, { stock }) => sum + (stock.available_qty ?? 0), 0);
  // Nebim fixture contains zero cost fields; zero is not a usable cost and must not be presented as confirmed valuation.
  const stockValue = validSnapshotRows.reduce((sum, { stock }) => sum + (isUsableCost(stock.cost_total) ? stock.cost_total : 0), 0);
  const hasCostValues = validSnapshotRows.some(({ stock }) => isUsableCost(stock.cost_total));

  const exportSnapshot = () => {
    const exportRows = filteredRows.map(({ stock, row_status, source_row_number }) => ({
      "Kaynak Satır": source_row_number,
      "Depo": stock.store_name ?? "",
      "Ürün Kodu": stock.sku ?? "",
      "Barkod": stock.barcode ?? "",
      "Ürün Adı": stock.product_name ?? "",
      "Renk Kodu": stock.color_code ?? "",
      "Renk": stock.color_name ?? "",
      "Beden": stock.size ?? "",
      "Envanter": stock.inventory_qty ?? "",
      "Kullanılabilir Envanter": stock.available_qty ?? "",
      "Rezerve": stock.reserved_qty ?? "",
      "Bekleyen Sevk": stock.remaining_shipment_qty ?? "",
      "Maliyet Fiyatı": isUsableCost(stock.cost_price) ? stock.cost_price : "",
      "Maliyet Tutarı": isUsableCost(stock.cost_total) ? stock.cost_total : "",
      "Satır Durumu": row_status,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stok Snapshot");
    XLSX.writeFile(workbook, "Kartalyuvasi_IFM_Stok_Snapshot.xlsx");
  };

  return (
    <div data-testid="page-inventory" className="space-y-8">
      <PageHeader
        eyebrow="Analiz"
        title="Stok ve Envanter"
        description="En son içe aktarılan Nebim stok anlık görüntüsü; arama, depo filtresi ve Excel dışa aktarma."
        actions={
          <Button variant="outline" size="sm" onClick={() => void loadSnapshot()} disabled={loading || !factory.ok || !companyId}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Yenile
          </Button>
        }
      />

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <strong>Stok snapshot'ı ve fatura hareketleri ayrı gösterilir.</strong> Aşağıdaki envanter değerleri son Nebim dosyasından gelir. Snapshot sonrasında kesinleşen faturaların ürün girişleri ayrıca listelenir; varyant bilgisi olmayan hareketler beden/renk satırlarına dağıtılmadığından mevcut miktarlar otomatik artırılmaz ve mükerrer stok riski oluşturulmaz.
      </div>

      {error ? <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">Stok verisi yüklenemedi: {error}</div> : null}

      {batch ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm">
          <div className="font-medium text-foreground">Son stok dosyası: {batch.original_filename ?? "Dosya adı yok"}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            İçe aktarma: {new Date(batch.created_at).toLocaleString("tr-TR")} · Durum: {batch.status} · Satır: {formatQuantity(batch.total_rows)} · Hatalı: {formatQuantity(batch.error_rows)}
          </div>
        </div>
      ) : null}

      {postSnapshotMovements.length > 0 ? (
        <section aria-label="Snapshot sonrası fatura stok girişleri" className="space-y-3 rounded-lg border border-border bg-card p-5">
          <div>
            <h2 className="font-serif text-xl text-foreground">Nebim snapshot sonrası fatura girişleri</h2>
            <p className="text-sm text-muted-foreground">Yalnızca son snapshot'tan sonra kaydedilmiş, kesinleşmiş gerçek fatura hareketleri. Bu liste mevcut snapshot miktarlarına eklenmiş değildir.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border p-3"><div className="text-sm text-muted-foreground">Eklenen ürün adedi (hareket toplamı)</div><div className="num mt-1 text-2xl font-medium">{formatQuantity(postSnapshotMovements.reduce((sum, item) => sum + item.quantity, 0))}</div></div>
            <div className="rounded-md border border-border p-3"><div className="text-sm text-muted-foreground">KDV hariç fatura maliyeti</div><div className="num mt-1 text-2xl font-medium">{formatCurrencyAmount(postSnapshotMovements.reduce((sum, item) => sum + item.net_cost, 0))}</div></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary/50 text-xs text-muted-foreground"><tr><th className="px-3 py-2">Fatura</th><th className="px-3 py-2">Ürün kodu</th><th className="px-3 py-2">Ürün adı</th><th className="px-3 py-2 text-right">Giriş adedi</th><th className="px-3 py-2 text-right">KDV hariç maliyet</th></tr></thead>
              <tbody className="divide-y divide-border">{postSnapshotMovements.map((item) => <tr key={`${item.invoice_no}|${item.product_code}`}><td className="px-3 py-2">{item.invoice_no}</td><td className="px-3 py-2 font-mono text-xs">{item.product_code}</td><td className="px-3 py-2">{item.product_name}</td><td className="px-3 py-2 text-right tabular-nums">{formatQuantity(item.quantity)}</td><td className="px-3 py-2 text-right tabular-nums">{formatCurrencyAmount(item.net_cost)}</td></tr>)}</tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section aria-label="Stok filtreleri" className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-3">
        <div className="relative md:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="SKU, barkod, ürün, renk veya beden ara"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={loading || snapshotRows.length === 0}
            className="pl-9"
            data-testid="inventory-search-input"
          />
        </div>
        <Select value={location} onValueChange={setLocation} disabled={loading || locations.length === 0}>
          <SelectTrigger data-testid="inventory-location-filter">
            <SelectValue placeholder="Depo seçiniz" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm depolar</SelectItem>
            {locations.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
      </section>

      <section aria-label="Stok özet kartları" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Ürün varyantı", value: formatQuantity(uniqueVariants) },
          { label: "Toplam envanter", value: formatQuantity(totalInventory) },
          { label: "Kullanılabilir adet", value: formatQuantity(availableInventory) },
          { label: "Maliyet tutarı", value: hasCostValues ? formatCurrencyAmount(stockValue) : "—" },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border border-border bg-card p-5">
            <div className="text-sm text-muted-foreground">{card.label}</div>
            <div className="num mt-2 text-3xl font-medium text-foreground">{loading && rows.length === 0 ? "…" : card.value}</div>
            <div className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">{batch ? "Son staging snapshot" : "Veri bekleniyor"}</div>
          </div>
        ))}
      </section>

      <section aria-label="Envanter tablosu" className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="font-serif text-xl text-foreground">Ürün Listesi</h2>
            <p className="text-sm text-muted-foreground">{filteredRows.length.toLocaleString("tr-TR")} satır gösteriliyor; kaynak dosyanın ilk içe aktarılmış snapshot'ı kullanılır.</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportSnapshot} disabled={filteredRows.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Excel'e aktar
          </Button>
        </div>
        {loading && rows.length === 0 ? <div className="p-6 text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Stok satırları yükleniyor…</div> : null}
        {!loading && !error && snapshotRows.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={PackageSearch} title="Stok snapshot'ı bulunamadı" description="Nebim Excel İçe Aktarma ekranından gerçek IFM depo dosyasını yükleyip staging'e kaydettikten sonra ürünler burada listelenecek." testId="inventory-table-empty" />
          </div>
        ) : null}
        {filteredRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-3">Depo</th><th className="px-3 py-3">Ürün kodu</th><th className="px-3 py-3">Barkod</th><th className="px-3 py-3">Ürün adı</th><th className="px-3 py-3">Renk / Beden</th><th className="px-3 py-3 text-right">Envanter</th><th className="px-3 py-3 text-right">Kullanılabilir</th><th className="px-3 py-3 text-right">Maliyet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredRows.slice(0, 500).map(({ id, stock, row_status }) => (
                  <tr key={id}>
                    <td className="px-3 py-3">{stock.store_name ?? "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs">{stock.sku ?? "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs">{stock.barcode ?? "—"}</td>
                    <td className="px-3 py-3">{stock.product_name ?? "—"}</td>
                    <td className="px-3 py-3">{[stock.color_name, stock.size].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatQuantity(stock.inventory_qty)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatQuantity(stock.available_qty)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{isUsableCost(stock.cost_total) ? formatQuantity(stock.cost_total) : "—"} <span className="text-[10px] text-muted-foreground">{row_status !== "valid" ? row_status : stock.cost_price === 0 || stock.cost_total === 0 ? "maliyet doğrulanmamış" : ""}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRows.length > 500 ? <p className="border-t border-border p-3 text-xs text-muted-foreground">Ekran performansı için ilk 500 satır gösteriliyor. Excel'e aktar düğmesi filtrelenen tüm satırları dışa aktarır.</p> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default Inventory;
